import { push } from '@lagunovsky/redux-react-router'
import { Html5Qrcode } from 'html5-qrcode'
import { all, call, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects'

import { LIST_OF_CUSTOM_PROVIDERS } from 'components/Forms/FormCustomBenefitPayment/FormCustomBenefitPayment'
import { startLoading, stopLoading } from 'components/Loader/LoaderPopup/reducer'
import { setErrorMessage } from 'components/Windows/CentralMessagesList/reducer'
import { verifyPayment } from 'pages/PaymentVerify/saga'
import { callApiAsync } from 'services/api/callAPIAsync'
import gtmPurchase from 'services/gtm/gtmPurchase'
import { PATH_NAME } from '../../app/constants'
import store from '../../app/store'
import { basketGetDataRequestFailed, getData, setBasketData, ticketVoucherAdd } from '../../components/Basket/reducer'
import { API_LANGUAGE_MAPPING } from '../../global/i18n/constants'
import { localeSelector } from '../../global/i18n/selectors'
import { getUser, getUserError, getUserSuccess } from '../../services/user/reducer'
import { VOUCHER_QR_CODE_INPUT_ID } from './components/GiftVoucher/constants'
import {
    payTicketWithMoney,
    payTicketWithPoints,
    paymentStart,
    qrLoaded,
    reservationStart,
    setBookingData,
    setError,
    setLoading,
    setPayment,
} from './reducer'

/**
 * submit form from cinestar api
 */
function submitForm(form) {
    const formatedForm = form.replace(/\\"/g, '"').replace().replace(/\n/g, ' ')
    const formElement = new DOMParser().parseFromString(formatedForm, 'text/html').body.childNodes[0]
    window.document.body.appendChild(formElement)
    formElement.submit()
}

function* getPaymentsParams(paymenCreateResponse) {
    const payment_params = paymenCreateResponse.payment_params
    const benefitForm = yield select((state) => state.components.forms.benefitPayment)

    payment_params.ACCOUNT = benefitForm.account
    payment_params.PIN = benefitForm.pin
    return payment_params
}

function* paymentStartCallSaga(action) {
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null
    const user = action?.payload?.user

    const benefitForm = yield select((state) => state.components.forms.benefitPayment)
    const selectedPayment = yield select((state) => state.components.basket.data.selected_payment)
    const noBenefitFormData = benefitForm.account === '' || benefitForm.pin === ''
    if (LIST_OF_CUSTOM_PROVIDERS.includes(selectedPayment) && noBenefitFormData) {
        yield put(setErrorMessage('NO_PAYMENT_DATA'))
        return
    }

    yield put(setLoading(true))
    yield put(startLoading())

    const { data: bookingData, error: bookingError } = yield call(callApiAsync, 'bookingCreate', {
        language,
        firstname: user.firstname,
        lastname: user.lastname,
        phone: user.phone,
        email: user.email,
    })

    const transactionId = bookingData.bookingid
    yield call(gtmPurchase, transactionId)

    if (bookingError) {
        console.error('paymentStartCallSaga', 'createBookingResult', bookingError)
        yield put(setError({ internal: true, message: bookingError }))
        yield put(setLoading(false))
        yield put(push('/error'))
        yield put(stopLoading())
        return
    }

    localStorage.setItem('activeBookingId', bookingData.bookingid)

    const bookingId = bookingData.bookingid
    const orderId = bookingData.orderid

    // (EG: If used points or vouchers and remaining cost is zero.)
    if (orderId > 0) {
        yield put(push(`${PATH_NAME.THANKS_FOR_ORDER}?orderid=${orderId}`))
        yield put(stopLoading())
        return
    }

    const url = window.location.origin

    const payment_url_return = `${url}${PATH_NAME.VERIFY_PAYMENT}`
    const payment_failed_url_return = `${url}${PATH_NAME.PAYMENT_FAILED}`
    localStorage.removeItem('user-info')

    const additional_payment_params = {}
    const goPayVariant = yield select((state) => state?.pages?.orderSummary?.goPayVariant)

    if (goPayVariant === 'GooglePay') {
        additional_payment_params.DefaultPaymentChannel = 'GPAY'
    }

    if (goPayVariant === 'ApplePay') {
        additional_payment_params.GopayPaymentChannelIds = 'APPLE_PAY'
        additional_payment_params.DefaultPaymentChannel = 'APPLE_PAY'
    }

    if (goPayVariant === 'Card') {
        additional_payment_params.GopayPaymentChannelIds = 'APPLE_PAY,PAYMENT_CARD'
    }

    if (goPayVariant === 'BankAccount') {
        additional_payment_params.GopayPaymentChannelIds = 'BANK_ACCOUNT'
    }

    if (goPayVariant === 'ClickToPay') {
        additional_payment_params.GopayPaymentChannelIds = 'PAYMENT_CARD,CLICK_TO_PAY'
        additional_payment_params.DefaultPaymentChannel = 'CLICK_TO_PAY'
    }

    const { data: paymentData, error: paymentError } = yield call(callApiAsync, 'paymentCreate', {
        bookingid: bookingId,
        language,
        payment_url_return,
        payment_failed_url_return,
        additional_payment_params,
    })

    if (paymentError) {
        console.error('paymentStartCallSaga', 'createPaymentResult', paymentError)
        yield put(setError({ internal: true, message: paymentError }))
        yield put(setLoading(false))
        yield put(stopLoading())
        return
    }

    const paymentUrl = paymentData?.payment_params?.UrlPaymentGate

    // Verifying payment here, because we are sending sensitive information (PIN and CARD number should not be in url search).
    if (paymentData.use_own_payment_form) {
        const verifyBody = yield call(getPaymentsParams, paymentData)
        yield call(verifyPayment, { provider_data: verifyBody })
        yield put(setLoading(false))
        yield put(stopLoading())
        return
    }

    //  GOPAY

    if (paymentData.payment_type === 'GoPay') {
        try {
            // global method from form script
            yield _gopay.checkout({ gatewayUrl: paymentUrl, inline: true }, (goPayResult) =>
                store.dispatch(push(goPayResult?.url))
            )
        } catch (err) {
            console.error('paymentStartCallSaga', err)
            yield put(setError({ internal: true, message: err }))
        }
    }

    if (paymentData.payment_type === 'MuzaPay') {
        window.location.href = paymentData.payment_params.gatewayUrl
    }

    //  SODEXO = Cafeteria

    if (paymentData.payment_type === 'Cafeteria') {
        window.location.href = `${paymentData.payment_params.URL}?EShopOrderId=${paymentData.payment_params.EShopOrderId}&BenefitsPrice=${paymentData.payment_params.BenefitsPrice}`
    }

    // BenefityPlus requires form submit

    if (paymentData.payment_type === 'BenefityPlus') {
        submitForm(paymentData.html_form)
    }

    if (paymentData.payment_type === 'EdenRed') {
        submitForm(paymentData.html_form)
    }

    yield put(getData())
    yield put(stopLoading())
    yield put(setLoading(false))
}

function* reservationStartCallSaga({ payload }) {
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null
    const { firstname, lastname, phone, email } = payload

    yield put(setLoading(true))
    yield put(setError(null))

    const { data: bookingData, error: bookingError } = yield call(callApiAsync, 'bookingCreate', {
        language,
        firstname,
        lastname,
        phone,
        email,
    })

    if (bookingError) {
        console.error('Error in booking step', bookingError)
        yield put(setError({ internal: true, message: bookingError }))
        yield put(setLoading(false))
        return
    }

    const transactionId = bookingData.bookingid
    yield call(gtmPurchase, transactionId)

    yield put(getData())
    yield put(push(`/tickets-reserved?bookingId=${bookingData.bookingid}&bookingNumber=${bookingData.booking_number}`))

    yield all([put(setLoading(false)), put(setBasketData({})), put(setBookingData(bookingData))])
    localStorage.setItem('user-info', null)
}

function* setPaymentCallSaga({ payload }) {
    const payment_type = payload
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null

    yield put(setLoading(true))
    const { error } = yield call(callApiAsync, 'setPayment', { payment_type, language })
    yield put(setLoading(false))

    if (error) {
        console.error(error)
        yield put(setErrorMessage(error))
        return
    }

    yield put(getData(language))
}

function* voucherQRCodeLoadedSaga(action) {
    const qrCode = action?.payload

    const html5QrCode = new Html5Qrcode(VOUCHER_QR_CODE_INPUT_ID)
    try {
        const result = yield call([html5QrCode, 'scanFile'], qrCode)
        yield put(ticketVoucherAdd({ voucher_number: result }))
    } catch (error) {
        yield put(setErrorMessage('CANT_READ_QR_CODE'))
        console.error(error)
    }
}

function* ticketPaymentByPointsCallSaga(action) {
    const ticketid = action?.payload
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null

    if (_.isNil(ticketid)) {
        console.error('ticketPaymentByPointsCallSaga', 'No ticket ID found')
        yield put(push('/error'))
        return
    }

    yield put(setLoading(true))

    const { error } = yield call(callApiAsync, 'ticketPointPayment', { ticketid, language })

    yield put(setLoading(false))

    if (error) {
        yield put(setErrorMessage(error))
        console.error('ticketPaymentByPointsCallSaga', error)
        return
    }

    yield put(getData())

    const { failed: basketFailed } = yield race({
        success: take(setBasketData.type),
        failed: take(basketGetDataRequestFailed.type),
    })

    if (basketFailed) {
        console.error('ticketPaymentByPointsCallSaga', 'Basket get data request failed')
        yield put(push('/error'))
        return
    }

    yield put(getUser())

    const { failed: userFailed } = yield race({
        success: take(getUserSuccess.type),
        failed: take(getUserError.type),
    })

    if (userFailed) {
        console.error('ticketPaymentByPointsCallSaga', 'Get user request failed')
        yield put(push('/error'))
        return
    }
}

function* ticketPaymentByMoneyCallSaga(action) {
    const ticketid = action?.payload
    const language = API_LANGUAGE_MAPPING?.[yield select(localeSelector)] ?? null

    if (_.isNil(ticketid)) {
        console.error('ticketPaymentByMoneyCallSaga', 'No ticket ID found')
        yield put(push('/error'))
        return
    }

    yield put(setLoading(true))

    const { ticketMoneyError } = yield call(callApiAsync, 'ticketMoneyPayment', { ticketid, language })

    yield put(setLoading(false))

    if (ticketMoneyError) {
        yield put(setErrorMessage(ticketMoneyError))
        console.error('ticketPaymentByMoneyCallSaga', ticketMoneyError)
        return
    }

    yield put(getData())

    const { failed: basketFailed } = yield race({
        success: take(setBasketData.type),
        failed: take(basketGetDataRequestFailed.type),
    })

    if (basketFailed) {
        console.error('ticketPaymentByMoneyCallSaga', 'Basket get data request failed')
        yield put(push('/error'))
        return
    }

    yield put(getUser())

    const { failed: userFailed } = yield race({
        success: take(getUserSuccess.type),
        failed: take(getUserError.type),
    })

    if (userFailed) {
        console.error('ticketPaymentByMoneyCallSaga', 'Get user request failed')
        yield put(push('/error'))
        return
    }
}

export default function* orderSummarySaga() {
    yield all([
        takeLatest(paymentStart.type, paymentStartCallSaga),
        takeLatest(reservationStart.type, reservationStartCallSaga),
        takeLatest(qrLoaded.type, voucherQRCodeLoadedSaga),
        takeLatest(setPayment.type, setPaymentCallSaga),
        takeEvery(payTicketWithPoints.type, ticketPaymentByPointsCallSaga),
        takeEvery(payTicketWithMoney.type, ticketPaymentByMoneyCallSaga),
    ])
}
